/* eslint-disable no-console */
import { useMemo, useCallback } from 'react';
import { mergeDeep, cloneDeep } from '@apollo/client/utilities';
import { Reference } from '@apollo/client';
import { ClipFragmentDoc, ClipFragment } from '../../../fragments.generated';
import {
  useApolloCacheUtilities,
  getFragmentName,
} from '../../../../util/apolloUtils';
import { ClipObject } from '../../../../context/clips';
import { ClipsRefs } from './common';
import {
  ClipDeletionFragmentDoc,
  ClipDeletionFragment,
} from '../../operation.generated';

const logger = (enable: boolean): Pick<Console, 'info' | 'error'> => {
  return enable
    ? { info: console.info, error: console.error }
    : { info: () => {}, error: console.error };
};

const log = logger(false);

const fragmentName = getFragmentName(ClipFragmentDoc);

/**
 * A helper utility to interact with the cache for clips
 */
const useClipCacheUtilities = () => {
  const { client, identityInCache } = useApolloCacheUtilities();

  const readClipFragment = useCallback(
    (id: string) => {
      log.info(`readClipFragment: ${id}`);
      return client.readFragment<ClipFragment>({
        id: identityInCache('Clip', id),
        fragment: ClipFragmentDoc,
        fragmentName,
      });
    },
    [identityInCache, client]
  );

  const clipInCacheById = useCallback(
    (id: string) => {
      log.info(`clipInCacheById: ${id}`);
      const clip = readClipFragment(id);
      return clip || undefined;
    },
    [readClipFragment]
  );

  const clipExistsInCache = useCallback(
    (clipId?: string) => {
      log.info(`clipExistsInCache: ${clipId}`);
      return Boolean(clipId && !!clipInCacheById(clipId));
    },
    [clipInCacheById]
  );

  const writeClipFragment = useCallback(
    (clip: ClipObject, overrides?: Partial<ClipObject>) => {
      const overrideOrClipId = overrides?.id || clip.id;
      const id = identityInCache('Clip', overrideOrClipId);
      log.info(`writeClipFragment: ${id}`);
      return client.writeFragment<ClipFragment>({
        id,
        fragment: ClipFragmentDoc,
        fragmentName,
        data: clip,
      });
    },
    [client, identityInCache]
  );

  const writeClipFragmentRef = useCallback(
    (
      clip: ClipObject,
      overrides?: Partial<ClipObject>
    ): Reference | undefined => {
      const overrideOrClipId = overrides?.id || clip.id;
      const id = identityInCache('Clip', overrideOrClipId);
      const clipRef = client.cache.writeFragment<ClipFragment>({
        id,
        fragment: ClipFragmentDoc,
        fragmentName,
        data: clip,
      });
      log.info(`writeClipFragmentRef: ${clipRef}`);
      return clipRef;
    },
    [identityInCache, client]
  );

  const writeClipFragmentToProject = useCallback(
    (clipRef: Reference, projectById: string) => {
      log.info(`writeClipFragmentToProject: ${projectById}`);
      client.cache.modify({
        id: identityInCache('Project', projectById),
        fields: {
          clips_V2: (existingClipsRefs, { readField }) => {
            const newEdges = cloneDeep(existingClipsRefs.edges);
            newEdges.push({ node: clipRef });
            /**
             * Drawing special attention to this sort for 2 reasons
             * 1: this sort is here due to explicitly writing to the cache of projects
             * in a move context
             * 2. readField could not infer the node without establishing variables here.
             */
            newEdges.sort((a: any, b: any) => {
              const sequence1 = readField('sequence', a.node);
              const sequence2 = readField('sequence', b.node);
              if (sequence1 && sequence2) {
                return sequence1 < sequence2 ? 1 : -1;
              }
              console.warn(
                'writeClipFragmentToProject: sequence 1 or 2 was not defined'
              );
              return 0;
            });
            const clipWithNewEdgeResponse = mergeDeep<Array<ClipsRefs>>(
              existingClipsRefs,
              {
                edges: newEdges,
              }
            );
            return clipWithNewEdgeResponse;
          },
        },
      });
      const reference = client.cache.identify(clipRef);
      log.info(reference);
    },
    [client.cache, identityInCache]
  );

  const writeClipToCache = useCallback(
    (clip: ClipObject, projectById: string) => {
      const clipRef = writeClipFragmentRef(clip);
      log.info(
        `writeClipToCache: clipProjectId: ${clip.projectId}, projectId: ${projectById}`
      );
      if (!clipRef) {
        log.error('writeClipToCache: reference not able to be written');
        return;
      }
      writeClipFragmentToProject(clipRef, projectById);
    },
    [writeClipFragmentRef, writeClipFragmentToProject]
  );

  const removeClipFromCache = useCallback(
    (clip: Pick<ClipObject, 'id'>, projectById: string) => {
      log.info(
        `removeClipFromCache: clipId ${clip.id}, projectId: ${projectById}`
      );
      // avoid writing clip to cache if it already exists
      const exists = clipExistsInCache(clip.id);
      if (!exists) {
        log.error(`removeClipFromCache: clip ${clip.id} does not exist`);
        return;
      }
      client.cache.modify({
        id: identityInCache('Project', projectById),
        fields: {
          clips_V2: (existingClipsRefs, { readField }) => {
            return {
              ...existingClipsRefs,
              edges: existingClipsRefs.edges.filter(
                (edge: any) => clip.id !== readField('id', edge.node)
              ),
            };
          },
        },
      });
    },
    [client.cache, clipExistsInCache, identityInCache]
  );
  /**
   *
   * Note: not in use currently
   * This was meant to evict the item from the cache fully
   * However this poses issues with audio context
   */
  const removeClipFromCacheWithEviction = useCallback(
    (clip: ClipObject, projectById: string) => {
      removeClipFromCache(clip, projectById);
      log.info(
        `removeClipFromCacheWithEviction: ${identityInCache('Clip', clip.id)}`
      );
      client.cache.evict({ id: identityInCache('Clip', clip.id) });
      client.cache.gc();
    },
    [client.cache, identityInCache, removeClipFromCache]
  );

  const softDeleteClipFromCache = useCallback(
    (clip: ClipObject) => {
      log.info(`softDeleteClipFromCache:`);
      const foundClip = clipInCacheById(clip.id);
      if (!foundClip) {
        log.error(`softDeleteClipFromCache: clip ${clip.id} does not exist`);
        return;
      }
      client.writeFragment<ClipDeletionFragment>({
        id: identityInCache('Clip', foundClip.id),
        fragment: ClipDeletionFragmentDoc,
        data: {
          ...foundClip,
          isDeleted: true,
        },
      });
    },
    [client, clipInCacheById, identityInCache]
  );

  const updateClipInCache = useCallback(
    (clip: ClipObject, projectById?: string) => {
      log.info(`updateClipInCache:`);
      const clipToUpdate = clipInCacheById(clip.id);
      if (!clipToUpdate) {
        log.error(`updateClipInCache: clip ${clip.id} does not exist`);
        return;
      }
      writeClipFragment({
        ...clipToUpdate,
        ...clip,
        projectId: projectById || clip.projectId,
      });
    },
    [clipInCacheById, writeClipFragment]
  );

  const restoreClipInCache = useCallback(
    (clip: ClipObject) => {
      log.info(`restoreClipInCache:`);
      const clipToUpdate = clipInCacheById(clip.id);
      if (!clipToUpdate) {
        log.error(`restoreClipInCache: clip ${clip.id} does not exist`);
        return;
      }
      writeClipFragment({
        ...clipToUpdate,
        isDeleted: false,
      });
    },
    [clipInCacheById, writeClipFragment]
  );

  return useMemo(() => {
    return {
      clipExistsInCache,
      clipInCacheById,
      readClipFragment,
      removeClipFromCache,
      removeClipFromCacheWithEviction,
      restoreClipInCache,
      softDeleteClipFromCache,
      updateClipInCache,
      writeClipFragmentToProject,
      writeClipFragment,
      writeClipToCache,
      writeClipFragmentRef,
    };
  }, [
    clipExistsInCache,
    clipInCacheById,
    readClipFragment,
    removeClipFromCache,
    removeClipFromCacheWithEviction,
    restoreClipInCache,
    softDeleteClipFromCache,
    updateClipInCache,
    writeClipFragmentToProject,
    writeClipFragment,
    writeClipToCache,
    writeClipFragmentRef,
  ]);
};

export default useClipCacheUtilities;
